import React, { useState, useRef, useEffect } from 'react';

const PullToRefresh = ({ onRefresh, children, threshold = 80, disabled = false }) => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const scrollY = useRef(0);
  const containerRef = useRef(null);

  // Verificar si el usuario está en el top de la página
  const isAtTop = () => {
    return window.scrollY === 0 || scrollY.current === 0;
  };

  const handleTouchStart = (e) => {
    if (disabled || refreshing) return;
    
    // Solo iniciar si estamos en el top
    if (isAtTop()) {
      startY.current = e.touches[0].clientY;
      scrollY.current = window.scrollY;
    }
  };

  const handleTouchMove = (e) => {
    if (disabled || refreshing || startY.current === 0) return;
    
    const currentY = e.touches[0].clientY;
    const distance = currentY - startY.current;
    
    // Solo permitir pull si estamos en el top y jalando hacia abajo
    if (distance > 0 && isAtTop()) {
      setPulling(true);
      // Aplicar resistencia al pull (cuanto más jalas, más resistencia)
      const adjustedDistance = Math.min(distance * 0.5, threshold * 1.5);
      setPullDistance(adjustedDistance);
      
      // Prevenir scroll nativo si estamos pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = async () => {
    if (disabled || refreshing) return;
    
    // Si superamos el threshold, activar refresh
    if (pullDistance >= threshold) {
      setRefreshing(true);
      setPullDistance(threshold); // Mantener el indicador en posición
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Error en pull-to-refresh:', error);
      } finally {
        // Pequeño delay para feedback visual
        setTimeout(() => {
          setRefreshing(false);
          setPullDistance(0);
          setPulling(false);
        }, 300);
      }
    } else {
      // Animar de vuelta a 0
      setPullDistance(0);
      setPulling(false);
    }
    
    startY.current = 0;
  };

  // Limpiar estados si se desactiva
  useEffect(() => {
    if (disabled) {
      setPulling(false);
      setPullDistance(0);
      setRefreshing(false);
    }
  }, [disabled]);

  const pullProgress = Math.min(pullDistance / threshold, 1);
  const spinnerRotation = pullProgress * 360;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'relative',
        overflowY: 'auto'
      }}
    >
      {/* Indicador de Pull-to-Refresh */}
      <div
        style={{
          position: 'fixed',
          top: `max(env(safe-area-inset-top), 0px)`,
          left: '50%',
          transform: `translateX(-50%) translateY(${Math.min(pullDistance - 60, 0)}px)`,
          zIndex: 1000,
          opacity: pulling || refreshing ? 1 : 0,
          transition: pulling ? 'none' : 'all 0.3s ease',
          pointerEvents: 'none'
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(230, 44, 25, 0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(230, 44, 25, 0.3)',
            transform: refreshing ? 'scale(1)' : `scale(${0.5 + pullProgress * 0.5})`
          }}
        >
          {refreshing ? (
            // Spinner cuando está refrescando
            <div
              style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 0.6s linear infinite'
              }}
            />
          ) : (
            // Flecha que rota según el progreso
            <div
              style={{
                color: 'white',
                fontSize: '20px',
                transform: `rotate(${spinnerRotation}deg)`,
                transition: 'transform 0.1s ease'
              }}
            >
              ↓
            </div>
          )}
        </div>
        
        {/* Texto indicador */}
        {pulling && !refreshing && (
          <div
            style={{
              position: 'absolute',
              top: '50px',
              left: '50%',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              color: '#e62c19',
              fontSize: '12px',
              fontWeight: '600',
              textShadow: '0 1px 2px rgba(255,255,255,0.8)'
            }}
          >
            {pullDistance >= threshold ? '¡Suelta para actualizar!' : 'Arrastra para actualizar'}
          </div>
        )}
      </div>

      {/* Contenido */}
      {children}

      {/* CSS para animación del spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PullToRefresh;

